import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import SectionHeading from "@/components/SectionHeading";
import { LOCATIONS, TORONTO_SKYLINE, PHONE_TEL, PHONE_DISPLAY } from "@/constants/site";

export default function ServiceAreas() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section id="service-areas" ref={ref} className="relative overflow-hidden py-20 sm:py-28" data-testid="service-areas-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img
          src={TORONTO_SKYLINE}
          alt="Toronto skyline — GTA Appliance Rescue service area"
          className="h-[124%] w-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-navy/90" />
      </motion.div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          number="04"
          caption="Where We Roll"
          title="Toronto & The Entire GTA, Covered."
          sub="Our vans are already in your neighbourhood — that's how we get to you the same day you call."
          dark
        />

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {LOCATIONS.map((location, i) => (
            <motion.div
              key={location.slug}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.45, delay: (i % 4) * 0.06 }}
            >
              <Link
                to={`/areas/${location.slug}`}
                data-testid={`area-chip-${location.slug}`}
                className="flex h-full items-center gap-2.5 rounded-xl border border-white/15 bg-white/5 px-4 py-3.5 backdrop-blur-sm transition-colors duration-300 hover:border-red-400/50 hover:bg-white/10"
              >
                <MapPin className="h-4 w-4 shrink-0 text-rescue" />
                <span className="text-sm font-semibold text-white">{location.city}</span>
              </Link>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-6 text-sm text-slate-300"
        >
          Plus Milton, Ajax & Pickering, Whitby & Oshawa, Newmarket & Aurora, Oakville, Burlington, Woodbridge and more — call to confirm your address.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-12 flex flex-col items-start gap-5 rounded-2xl border border-white/15 bg-white/5 p-6 backdrop-blur-md sm:flex-row sm:items-center sm:justify-between sm:p-8"
        >
          <div>
            <p className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-red-400">
              Outside these areas?
            </p>
            <p className="mt-2 font-display text-xl font-bold text-white sm:text-2xl">
              Call anyway — we often make it work.
            </p>
          </div>
          <a
            data-testid="areas-call-button"
            href={PHONE_TEL}
            className="flex shrink-0 items-center gap-2.5 rounded-full bg-rescue px-7 py-3.5 text-sm font-bold text-white shadow-xl shadow-red-600/25 transition-[background-color,transform] duration-200 hover:bg-rescue-dark hover:-translate-y-0.5"
          >
            <Phone className="h-4 w-4" />
            {PHONE_DISPLAY}
          </a>
        </motion.div>
      </div>
    </section>
  );
}
